import React, { useState, useEffect } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { CFormLabel } from "@coreui/react";

// Contexts
import { useAuth } from "../contexts/AuthContext";
import { useCategory } from "../contexts/CategoryContext";

/**
 * PrinterCategorySelection
 * ----------------------------------------------------
 * Used while assigning categories to printers.
 * Fetches ONLY categories:
 * - Belonging to the restaurant
 * - Available for the selected floor
 * - Not already assigned to any printer on that floor
 */
function PrinterCategorySelection({
    value,
    onChange,
    floorId,
    className,
    labelClassName,
    multiSelect = true,
    required = false,
    error = false,
    placeholder = "Select category...",
    label = "Category",
    disabled = false,
}) {
    const { restaurantId } = useAuth();
    const { getAvailableCategoriesForPrinterByFloor } = useCategory();

    const requiredIcon = required ? <span className="text-danger">*</span> : null;

    const [key, setKey] = useState(0);

    // Reset select when floor changes
    useEffect(() => {
        setKey(prev => prev + 1);
    }, [floorId]);

    // Fetch categories from API
    const fetchCategories = async (searchValue, loadedOptions, { offset }) => {
        try {
            if (!restaurantId || !floorId) {
                return {
                    options: [],
                    hasMore: false,
                };
            }

            const response = await getAvailableCategoriesForPrinterByFloor({
                restaurant_id: restaurantId,
                floor_id: floorId,
                filter: searchValue,
                limit: 50,
                offset,
            });

            const categories = response?.categories || [];
            const totalCount = response?.totalCount || 0;

            const options = categories.map(cat => ({
                label: cat.name,
                value: cat.id,
            }));

            return {
                options,
                hasMore: offset + options.length < totalCount,
                additional: {
                    offset: offset + options.length,
                },
            };
        } catch (err) {
            console.error("Failed to fetch printer categories", err);
            return {
                options: [],
                hasMore: false,
            };
        }
    };

    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            whiteSpace: state.isFocused ? 'pre-wrap' : 'nowrap',
            wordBreak: state.isFocused ? 'break-all' : null,
            wordWrap: state.isFocused ? 'break-word' : null,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
    };

    return (
        <div key={key} className={className}>
            {label && (
                <div className={labelClassName}>
                    <CFormLabel>
                        {label} {requiredIcon}
                    </CFormLabel>
                </div>
            )}

            <div className="w-100">
                <AsyncPaginate
                    className={`${(error && required) && 'multiSelect_error'} `}
                    value={value}
                    loadOptions={fetchCategories}
                    isMulti={multiSelect}
                    closeMenuOnSelect={!multiSelect ? true : false}
                    onChange={onChange}
                    placeholder={placeholder}
                    isDisabled={disabled || !floorId}
                    noOptionsMessage={() => "No categories available"}
                    loadingMessage={() => "Loading categories..."}
                    additional={{
                        offset: 0,
                    }}
                    SelectProps={{
                        isSearchable: true,
                    }}
                    menuShouldScrollIntoView={false}
                    menuPortalTarget={document.querySelector('body')}
                    styles={customStyles}
                />

                {error && required && (
                    <span className="text-danger error_message">
                        Please select at least one category.
                    </span>
                )}
            </div>
        </div>
    );
}

export default PrinterCategorySelection;
